<script setup>
import {computed, ref, watch} from 'vue'
import { useRoute, useRouter } from 'vue-router'
import { useWorksStore } from '@/stores/work.js'
import {useToast} from "primevue";
import Card from 'primevue/card';
import SpeedDial from 'primevue/speeddial';
import Contacts from "@/components/Contacts.vue";
import {fancyDate} from "@/filters/fancyDate.js";
import ConfirmDeleteDialog from "@/components/common/ConfirmDeleteDialog.vue";
import {useLoadByRouteId} from "@/composables/useLoadByRouteId.js";
import ConfirmArchiveDialog from "@/components/common/ConfirmArchiveDialog.vue";
import WorkForm from '@/components/work/WorkForm.vue'
import { useResponsiblesStore } from '@/stores/responsibles.js';
import ResponsibilityList from '@/components/common/ResponsibilityList.vue';
import WorkflowStatus from "@/components/common/WorkflowStatus.vue";
import CreateReminderDialog from "@/components/common/CreateReminderDialog.vue";
import EntityMessageDialog from "@/components/messages/EntityMessageDialog.vue";
import TaskCreateDialog from "@/components/tasks/TaskCreateDialog.vue";
import EventHistoryDialog from "@/components/common/EventHistoryDialog.vue";
import { usePermissions } from '@/composables/usePermissions.js';

const route = useRoute()
const router = useRouter()
const worksStore = useWorksStore()
const responsiblesStore = useResponsiblesStore();
const { hasPermission } = usePermissions();

const showEditDialog = ref(false) // состояние для диалога редактирования
const showConfirmDelete = ref(false)
const showConfirmArchive = ref(false)
const showConfirmUnarchive = ref(false)
const showMessageDialog = ref(false)

const toast = useToast();

const work = computed(() => worksStore.work)
const workId = computed(() => work.value?.id);
const workResponsiblesRaw = computed(() => responsiblesStore.items('work', workId.value))
const isArchived = computed(() => work.value?.status === 'ARCHIVED')

const responsible = computed(() => {
  const list = workResponsiblesRaw.value || []
  if (!Array.isArray(list) || list.length === 0) {
    return { id: null, name: '' }
  }
  const item = list.find(r => r?.is_primary) ?? list[0]
  const u = item?.user
  const id = Number(u?.id)
  const name = u?.full_name
      || [u?.last_name, u?.first_name].filter(Boolean).join(' ')
      || u?.name
      || ''
  return { id: Number.isFinite(id) && id > 0 ? id : null, name }
})


function openResponsibleMessage() {
  if (!responsible.value?.id) {
    toast.add({ severity: 'warn', summary: 'Не указан ответственный', life: 2000 })
    return
  }
  showMessageDialog.value = true
}

const prevWorkId = ref(null);

watch(workId, (id) => {
  if (id && id !== prevWorkId.value) {
    prevWorkId.value = id;
    responsiblesStore.loadCurrent('work', id);
  }
}, { immediate: true });


useLoadByRouteId((id) => worksStore.loadWork(id))


const facilityId = computed(() => work.value?.facility?.id ?? work.value?.facility_id ?? null)
const customerId = computed(() => work.value?.customer_id ?? null)
const contractId = computed(() => work.value?.contract?.id ?? work.value?.contract_id ?? null)

const blockerLabels = {
  works: 'Работы',
  contracts: 'Договоры',
  contacts: 'Контакты',
  comments: 'Комментарии'
}

function formatBlockers(blockers) {
  if (!blockers || typeof blockers !== 'object') return ''
  return Object.entries(blockers)
      .map(([key, count]) => `${blockerLabels[key] || key} (${count})`)
      .join('\n')
}

async function onDeleteConfirm() {
  try {
    await worksStore.deleteWork(route.params.id)
    toast.add({
      severity: 'success',
      summary: 'Работа удалена',
      detail: 'Данные работы успешно удалены',
      life: 2500,
    });
    await router.push('/works')
  } catch (e) {
    if (e.response?.status === 409 && e.response?.data?.blockers) {
      const data = e.response.data
      const blockersText = formatBlockers(data.blockers)
      const detail = `Найдены связанные сущности.\n${blockersText}\n Используйте архивирование.`
      toast.add({
        severity: 'warn',
        summary: 'Удаление невозможно',
        detail,
        life: 6000,
      });
    } else {
      toast.add({
        severity: 'error',
        summary: 'Ошибка',
        detail: 'Не удалось удалить работу',
        life: 4000,
      });
    }
  }
}

async function onArchiveConfirm() {
  try {
    await worksStore.archiveWork(route.params.id)
    toast.add({
      severity: 'success',
      summary: 'Работа архивирована',
      detail: 'Работа перемещена в архив',
      life: 2500,
    })
    await router.push('/works')
  } catch (e) {
    toast.add({
      severity: 'error',
      summary: 'Ошибка',
      detail: 'Не удалось архивировать работу',
      life: 4000,
    })
  }
}

async function onUnarchiveConfirm() {
  try {
    await worksStore.unarchiveWork(route.params.id)
    toast.add({
      severity: 'success',
      summary: 'Работа возвращена из архива',
      detail: 'Статус работы изменён на ACTIVE',
      life: 2500,
    })
  } catch (e) {
    toast.add({
      severity: 'error',
      summary: 'Ошибка',
      detail: 'Не удалось вернуть работу из архива',
      life: 4000,
    })
  }
}

const showCreateReminder = ref(false)
const showCreateTask = ref(false)
const showEventHistory = ref(false)


const reminderTarget = computed(() => {
  if (!workId.value) return null
  return { app: 'work', model: 'work', objectId: Number(workId.value) }
})
const taskContext = computed(() => {
  if (!workId.value) return null
  return { app: 'work', model: 'work', objectId: Number(workId.value) }
})
const menuItems = computed(() => {
  const items = []

  // Удалить - требует work.delete_work
  if (hasPermission('work.delete_work')) {
    items.push({
      label: 'Удалить',
      icon: 'pi pi-trash',
      severity: 'danger',
      command: () => {
        showConfirmDelete.value = true
      }
    })
  }

  // Архивировать/Вернуть из архива - требует work.archive_work
  if (hasPermission('work.archive_work')) {
    items.push({
      label: isArchived.value ? 'Вернуть из архива' : 'Архивировать',
      icon: isArchived.value ? 'pi pi-sun' : 'pi pi-moon',
      severity: isArchived.value ? 'success' : 'danger',
      command: () => {
        const currentStatus = isArchived.value
        if (currentStatus) {
          showConfirmUnarchive.value = true
        } else {
          showConfirmArchive.value = true
        }
      }
    })
  }

  // Написать ответственному - доступно всем
  items.push({
    label: 'Написать ответственному',
    icon: 'pi pi-envelope',
    command: openResponsibleMessage
  })

  // Добавить в Договор - доступно всем (функционал не реализован)
  items.push({
    label: 'Добавить в Договор',
    icon: 'pi pi-file-plus',
    command: () => {
      alert('Добавить в Договор');
    }
  })

  // Создать напоминание - доступно всем
  items.push({
    label: 'Создать напоминание',
    icon: 'pi pi-bell',
    command: () => {
      showCreateReminder.value = true
    }
  })

  // Создать задачу - требует task.create_for_work
  if (hasPermission('task.create_for_work')) {
    items.push({
      label: 'Создать задачу',
      icon: 'pi pi-pen-to-square',
      command: () => { showCreateTask.value = true }
    })
  }

  // История - доступна всем
  items.push({
    label: 'История',
    icon: 'pi pi-history',
    command: () => {
      showEventHistory.value = true
    }
  })

  // Редактировать - требует work.change_work
  if (hasPermission('work.change_work')) {
    items.push({
      label: 'Редактировать',
      icon: 'pi pi-pencil',
      command: () => {
        showEditDialog.value = true
      }
    })
  }

  return items
});
</script>

<template>
  <div>
    <div class="flex flex-col gap-4">
      <Card>
        <template #title>
          <div class="flex justify-between gap-2 items-center relative">
            <span>{{ work?.name || '...' }}</span>
            <div style="position: relative; width: 44px; height: 44px;">
              <SpeedDial
                  :model="menuItems"
                  :radius="180"
                  type="quarter-circle"
                  direction="down-left"
                  :style="{ position: 'absolute', left: 0, bottom: 0 }"
                  buttonClass="p-button-text p-button-secondary"
                  :showIcon="'pi pi-ellipsis-v'"
                  :hideIcon="'pi pi-times'"
              />
            </div>
          </div>
        </template>

        <template #content>
          <div class="space-y-4">
            <div class="flex">
              <div class="w-4/5 space-y-4">
                <div>
                  <span class="font-semibold">Объект: </span>
                  <template v-if="facilityId">
                    <RouterLink :to="`/facilities/${facilityId}`">
                      {{ work?.facility?.name || '—' }}
                    </RouterLink>
                  </template>
                  <template v-else>
                    {{ work?.facility?.name || '—' }}
                  </template>
                </div>

                <div>
                  <span class="font-semibold">Адрес: </span>{{ work?.facility?.address || '—' }}
                </div>

                <div>
                  <span class="font-semibold">Заказчик: </span>
                  <template v-if="customerId">
                    <RouterLink :to="`/customers/${customerId}`">
                      {{ work?.customer || '—' }}
                    </RouterLink>
                  </template>
                  <template v-else>
                    {{ work?.customer || '—' }}
                  </template>
                </div>

                <div>
                  <span class="font-semibold">Договор: </span>
                  <template v-if="work?.contract?.number">
                    <template v-if="contractId">
                      <RouterLink :to="`/contracts/${contractId}`">
                        № {{ work.contract.number }}
                        <span v-if="work.contract.date"> от {{ fancyDate(work.contract.date, 'short') }}</span>
                      </RouterLink>
                    </template>
                    <template v-else>
                      № {{ work.contract.number }}
                      <span v-if="work.contract.date"> от {{ fancyDate(work.contract.date, 'short') }}</span>
                    </template>
                  </template>
                  <template v-else>—</template>
                </div>


                <WorkflowStatus
                    v-if="workId"
                    entity="work"
                    :id="workId"
                    size="small"
                    @changed="() => worksStore.loadWork(route.params.id)"
                />
              </div>


              <div class="text-right text-sm w-1/5">
                <p v-if="work?.term_start" class="flex items-center justify-end gap-2 text-orange-400">
                  <i class="pi pi-flag text-xs"></i>
                  <span>{{ fancyDate(work?.term_start, 'short') }}</span>
                </p>
                <p v-if="work?.term_end" class="flex items-center justify-end gap-2 text-lime-600 mt-2">
                  <i class="pi pi-flag-fill text-xs"></i>
                  <span>{{ fancyDate(work?.term_end, 'short') }}</span>
                </p>
              </div>
            </div>

            <ResponsibilityList
                :list="workResponsiblesRaw"
                singularTitle="Ответственный"
                pluralTitle="Ответственные"
                :removable="true"
                entityType="work"
                :entityId="workId"
                :showPriority="false"
                :allowAdd="true"

            />

            <Contacts
                v-if="work?.id"
                :id="work.id"
                target="work"
                :filters="{ contract: contractId }"
            />
          </div>
        </template>
      </Card>
    </div>

    <ConfirmDeleteDialog
        v-model:visible="showConfirmDelete"
        :workName="work?.name || ''"
        @confirm="onDeleteConfirm"
    />
    <ConfirmArchiveDialog
        v-model:visible="showConfirmArchive"
        mode="archive"
        :workName="work?.name || ''"
        @confirm="onArchiveConfirm"
    />
    <ConfirmArchiveDialog
        v-model:visible="showConfirmUnarchive"
        mode="unarchive"
        :workName="work?.name || ''"
        @confirm="onUnarchiveConfirm"
    />
    <WorkForm
              v-model:visible="showEditDialog"
              :workId="work?.id || null"
              :contractId="contractId"
              :contractTitle="work?.contract?.number || ''"
              @saved="() => worksStore.loadWork(route.params.id)"
          />
    <CreateReminderDialog
        v-if="reminderTarget"
        v-model="showCreateReminder"
        :targetAppLabel="reminderTarget.app"
        :targetModel="reminderTarget.model"
        :targetObjectId="reminderTarget.objectId"
    />
    <EntityMessageDialog
        v-model="showMessageDialog"
        v-if="work"
        :user-id="responsible.id"
        :responsible-name="responsible.name || 'Ответственный'"
        entity-type="work"
        :entity-id="work?.id"
        :entity-title="work?.short_name || work?.name || `#${work?.id}`"
        :entity-url="`/works/${work?.id}`"
    />
    <TaskCreateDialog
        v-if="taskContext"
        v-model="showCreateTask"
        :contextAppLabel="taskContext.app"
        :contextModel="taskContext.model"
        :contextId="taskContext.objectId"
        :defaultAssigneeId="responsible.id"
        :contextTitle="work?.short_name || work?.name || `#${workId}`"
        @created="() => {/* опционально: обновить локальный список задач блока сущности, если он есть */}"
    />
    <EventHistoryDialog
        v-if="workId"
        v-model:visible="showEventHistory"
        target="work"
        :entityId="workId"
        :entityName="work?.name || ''"
    />
  </div>
</template>


<style scoped>
:deep(.p-card-body) {
  padding-top: 0;
  padding-bottom: 8px;
}
</style>